import { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Trophy, User as UserIcon, Users, Building, Calendar, ChevronDown, ChevronUp, ChevronLeft, ChevronRight } from 'lucide-react';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { useOrganization } from '@/hooks/use-organization';
import { cn } from '@/lib/utils';
import { getBeneficiaryCompetitions } from '@/lib/firebase/queries/competitions';
import {
  loadCompetitionStandings,
  loadStandingUsers,
  type IndividualStanding,
  type StandingUser,
} from '@/lib/firebase/queries/competitionStandings';
import { buildContainerMemberRankingPure, formatAverage, type ContainerStandingRow } from '@/lib/competitions/competitionRanking';
import { formatCompetitionDateRange, sliceTopWithMe, type TopSlice } from '@/lib/competitions/standingsView';
import { onCompetitionLogChanged } from '@/lib/competitions/competitionLogEvents';
import type { BeneficiaryCompetition } from '@/lib/competitions/beneficiaryCompetitions';
import type { Competition } from '@/types/competitions';
import { BeneficiaryCompetitionDetailsDialog } from '@/components/dashboard/BeneficiaryCompetitionDetailsDialog';

interface CompetitionRankingWidgetProps {
  organizationId?: string;
}

/**
 * One results card per competition the beneficiary can see — running ones and
 * recently ended ones (namat#33) — like mobile's CompetitionsWidget (namat#36):
 * dates, an «ended» badge, the top COMPETITION_TOP_N (5) of each tab with «(أنت)»
 * on the viewer's row, or «···» and the viewer's own row below them.
 * «أفضل 10 والإحصاءات» opens the full details dialog, and a group or section
 * row expands its own member ranking in place (mobile CompetitionsWidget).
 */
export function CompetitionRankingWidget({ organizationId }: CompetitionRankingWidgetProps) {
  const [competitions, setCompetitions] = useState<BeneficiaryCompetition<Competition>[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();
  const { organization, loading: orgLoading } = useOrganization();

  // Use provided organizationId or fall back to organization from hook
  const effectiveOrganizationId = organizationId || organization?.id;

  useEffect(() => {
    if (orgLoading) return;
    if (!currentUser || !effectiveOrganizationId) {
      setCompetitions([]);
      setIsLoading(false);
      return;
    }
    let cancelled = false;
    setIsLoading(true);
    getBeneficiaryCompetitions(effectiveOrganizationId)
      .then((list) => { if (!cancelled) setCompetitions(list); })
      .catch((error) => {
        console.error('Error loading competitions:', error);
        if (!cancelled) setCompetitions([]);
      })
      .finally(() => { if (!cancelled) setIsLoading(false); });
    return () => { cancelled = true; };
  }, [currentUser, effectiveOrganizationId, orgLoading]);

  if (isLoading || orgLoading) {
    return (
      <Card className="overflow-hidden" dir={dir}>
        <CardHeader className="border-b bg-muted/50">
          <CardTitle className="flex items-center gap-2">
            <Trophy className="h-5 w-5 text-[#7dda55]" />
            {t('dashboard.competition.title')}
          </CardTitle>
        </CardHeader>
        <CardContent className="p-4">
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  if (!currentUser || !effectiveOrganizationId || competitions.length === 0) {
    return null;
  }

  return (
    <div className="space-y-4">
      {competitions.map((competition) => (
        <CompetitionStandingsCard
          key={competition.id}
          competition={competition}
          organizationId={effectiveOrganizationId}
          currentUserId={currentUser.uid}
        />
      ))}
    </div>
  );
}

interface Standings {
  individuals: TopSlice<IndividualStanding>;
  groups: TopSlice<ContainerStandingRow>;
  sections: TopSlice<ContainerStandingRow>;
  users: Map<string, StandingUser>;
  /** Kept from the standings read so a tapped group/section ranks its members with no new query. */
  perUser: IndividualStanding[];
  groupOf: Map<string, string | null>;
  sectionOf: Map<string, string | null>;
}

type ContainerKind = 'group' | 'section';

function CompetitionStandingsCard({
  competition,
  organizationId,
  currentUserId,
}: {
  competition: BeneficiaryCompetition<Competition>;
  organizationId: string;
  currentUserId: string;
}) {
  const [standings, setStandings] = useState<Standings | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  // One expanded group/section at a time (accordion), like mobile: `${kind}|${id}`.
  const [expandedKey, setExpandedKey] = useState<string | null>(null);
  const { t, dir, language } = useLanguage();
  const isRTL = dir === 'rtl';
  // Latest request wins: a refresh fired by a quick log/unlog pair must not
  // be overwritten by the slower read that started before it.
  const requestId = useRef(0);

  const load = useCallback(async () => {
    const mine = ++requestId.current;
    try {
      // One read, ranked with the rule shared with mobile and the server
      // (namat#29/#32): everyone in the organization, groups by score per member.
      const full = await loadCompetitionStandings(competition.id, organizationId, currentUserId, competition);
      const individuals = sliceTopWithMe(full.individuals, (p) => p.userId === currentUserId);
      const shown = [...individuals.top, ...(individuals.me ? [individuals.me] : [])].map((p) => p.userId);
      const users = new Map(full.participantUsers);
      const missing = shown.filter((id) => !users.has(id));
      if (missing.length > 0) (await loadStandingUsers(missing)).forEach((u, id) => users.set(id, u));
      if (mine !== requestId.current) return;
      setStandings({
        individuals,
        groups: sliceTopWithMe(full.groups, (g) => g.isMe),
        sections: sliceTopWithMe(full.sections, (g) => g.isMe),
        users,
        perUser: full.individuals,
        groupOf: full.groupOf,
        sectionOf: full.sectionOf,
      });
    } catch (error) {
      console.error('Error loading competition standings:', error);
    } finally {
      if (mine === requestId.current) setIsLoading(false);
    }
  }, [competition, organizationId, currentUserId]);

  useEffect(() => {
    setIsLoading(true);
    void load();
  }, [load]);

  // Standings move with every log or un-log on this page (mobile refreshes
  // them after each toggle). Quietly: the current table stays until the new
  // one arrives.
  useEffect(
    () => onCompetitionLogChanged((competitionId) => {
      if (competitionId === competition.id) void load();
    }),
    [load, competition.id]
  );

  const userName = (userId: string) => {
    const u = standings?.users.get(userId);
    if (!u) return '';
    return isRTL
      ? `${u.firstNameAr || u.firstName} ${u.lastNameAr || u.lastName}`.trim()
      : `${u.firstNameEn || u.firstName} ${u.lastNameEn || u.lastName}`.trim();
  };

  const getInitials = (u: StandingUser | undefined) =>
    `${(u?.firstNameEn || u?.firstName || '').charAt(0)}${(u?.lastNameEn || u?.lastName || '').charAt(0)}`.toUpperCase();

  const rankIcon = (rank: number) => {
    if (rank === 1) return <Trophy className="h-5 w-5 text-yellow-500" />;
    if (rank === 2) return <Trophy className="h-5 w-5 text-gray-400" />;
    if (rank === 3) return <Trophy className="h-5 w-5 text-amber-600" />;
    return null;
  };

  const youSuffix = ` (${t('dashboard.competition.you')})`;
  const rowClass = (isMe: boolean) =>
    cn('flex items-center gap-4 p-3 rounded-lg', isMe ? 'bg-[#7dda55]/15 border border-[#7dda55]/40' : 'bg-muted/50');
  const gap = <div className="text-center text-muted-foreground leading-none" aria-hidden>···</div>;

  const IndividualRow = ({ p }: { p: IndividualStanding }) => {
    const isMe = p.userId === currentUserId;
    const user = standings?.users.get(p.userId);
    return (
      <div className={rowClass(isMe)}>
        <div className="flex items-center gap-2">
          {rankIcon(p.rank)}
          <Avatar className="w-10 h-10">
            <AvatarImage
              src={user?.photoURL}
              alt={userName(p.userId)}
              onError={(e) => {
                e.currentTarget.style.display = 'none';
              }}
            />
            <AvatarFallback className="bg-[#7dda55] text-white flex items-center justify-center">
              {user?.photoURL ? getInitials(user) : <UserIcon className="h-5 w-5" />}
            </AvatarFallback>
          </Avatar>
        </div>
        <div className="flex-1 min-w-0">
          <div className={cn('truncate', isMe ? 'font-bold' : 'font-medium')}>
            {userName(p.userId)}
            {isMe && youSuffix}
          </div>
          <div className="text-sm text-muted-foreground">
            {t('dashboard.competition.completedHabits')}: {p.completed}
          </div>
        </div>
        <div className="text-2xl font-bold text-[#7dda55]">{p.rank}</div>
      </div>
    );
  };

  const containerRow = (r: ContainerStandingRow, Icon: typeof Users, kind: ContainerKind) => {
    const key = `${kind}|${r.id}`;
    const isOpen = expandedKey === key;
    const Chevron = isOpen ? ChevronUp : ChevronDown;
    const panelId = `competition-${competition.id}-${kind}-${r.id}-members`;
    // The container's full member ranking, from the standings already in memory.
    const members = isOpen && standings
      ? buildContainerMemberRankingPure(currentUserId, r.id, kind === 'group' ? standings.groupOf : standings.sectionOf, standings.perUser)
      : [];
    return (
      <div key={r.id}>
        <button
          type="button"
          onClick={() => setExpandedKey(isOpen ? null : key)}
          aria-expanded={isOpen}
          aria-controls={panelId}
          data-testid={`competition-container-row-${kind}-${r.id}`}
          className={cn(rowClass(r.isMe), 'w-full text-start hover:bg-muted transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7dda55]')}
        >
          <div className="flex items-center gap-2">
            {rankIcon(r.rank)}
            <div className="flex items-center justify-center w-10 h-10 bg-[#7dda55] rounded-full">
              <Icon className="h-5 w-5 text-white" aria-hidden />
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className={cn('truncate', r.isMe ? 'font-bold' : 'font-medium')}>
              {r.name}
              {r.isMe && youSuffix}
            </div>
            <div className="text-sm text-muted-foreground">
              {t('dashboard.competition.perMember')}: {formatAverage(r.average)} • {t('dashboard.competition.total')} {r.completed} • {r.memberCount} {t('dashboard.competition.members')}
            </div>
          </div>
          <div className="text-2xl font-bold text-[#7dda55]">{r.rank}</div>
          <Chevron className="h-4 w-4 text-muted-foreground shrink-0" aria-hidden />
        </button>
        {isOpen && (
          <ol
            id={panelId}
            data-testid={`competition-container-members-${kind}-${r.id}`}
            aria-label={t('dashboard.competition.memberRanking', { name: r.name })}
            className="mt-1 ms-6 rounded-lg bg-muted/40 py-1"
          >
            {members.length === 0 ? (
              <li className="px-3 py-2 text-sm text-muted-foreground">{t('dashboard.competition.noParticipants')}</li>
            ) : (
              members.map((m) => (
                <li
                  key={m.userId}
                  className={cn('flex items-center gap-3 px-3 py-1.5 rounded-md text-sm', m.isMe && 'bg-[#7dda55]/15')}
                >
                  <span className="w-6 text-center font-bold tabular-nums">{m.rank}</span>
                  <span className={cn('flex-1 truncate', m.isMe ? 'font-bold' : 'font-medium')}>
                    {userName(m.userId) || t('dashboard.manager.competitions.insights.unknown')}
                    {m.isMe && youSuffix}
                  </span>
                  <span className="font-bold text-[#7dda55] tabular-nums">{m.completed}</span>
                </li>
              ))
            )}
          </ol>
        )}
      </div>
    );
  };

  const empty = (Icon: typeof Users, hint?: string) => (
    <div className="text-center py-8 text-muted-foreground">
      <Icon className="h-12 w-12 mx-auto mb-4 opacity-50" />
      <p>{t('dashboard.competition.noParticipants')}</p>
      {hint && <p className="text-sm mt-1">{hint}</p>}
    </div>
  );

  const containers = (slice: TopSlice<ContainerStandingRow> | undefined, Icon: typeof Users, kind: ContainerKind) =>
    !slice || slice.top.length === 0 ? (
      empty(Icon)
    ) : (
      <div className="space-y-3">
        {slice.top.map((r) => containerRow(r, Icon, kind))}
        {slice.me && (
          <>
            {gap}
            {containerRow(slice.me, Icon, kind)}
          </>
        )}
      </div>
    );

  const tabs = [
    { value: 'customers', Icon: UserIcon, label: t('dashboard.competition.individuals') },
    { value: 'groups', Icon: Users, label: t('dashboard.competition.groups') },
    { value: 'sections', Icon: Building, label: t('dashboard.competition.sections') },
  ];

  return (
    <Card className="overflow-hidden" dir={dir}>
      <CardHeader className="border-b bg-muted/50 space-y-1">
        <CardTitle className="flex items-center gap-2">
          <Trophy className="h-5 w-5 text-[#7dda55]" />
          <span className="truncate">{competition.name}</span>
          {competition.hasEnded && (
            <Badge variant="secondary" className="ms-auto bg-muted text-muted-foreground">
              {t('dashboard.competition.ended')}
            </Badge>
          )}
        </CardTitle>
        <div className="flex items-center gap-2 text-sm text-muted-foreground">
          <Calendar className="h-4 w-4" aria-hidden />
          <span className="flex-1">{formatCompetitionDateRange(competition.startDate, competition.endDate, language)}</span>
          {/* Full top 10 + stats + insights (mobile CompetitionDetailsSheet). */}
          <button
            type="button"
            onClick={() => setDetailsOpen(true)}
            aria-haspopup="dialog"
            data-testid={`competition-details-open-${competition.id}`}
            className="inline-flex items-center gap-0.5 text-xs font-bold text-foreground hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#7dda55] rounded"
          >
            {t('dashboard.competition.details.open')}
            {isRTL ? <ChevronLeft className="h-4 w-4" aria-hidden /> : <ChevronRight className="h-4 w-4" aria-hidden />}
          </button>
        </div>
      </CardHeader>
      <CardContent className="p-4">
        {isLoading && !standings ? (
          <div className="flex items-center justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
          </div>
        ) : (
          <Tabs defaultValue="customers" className="w-full">
            {/* Tabs follow the page direction: dir on the Card mirrors them in Arabic. */}
            <TabsList className="grid w-full grid-cols-3">
              {tabs.map(({ value, Icon, label }) => (
                <TabsTrigger key={value} value={value} className="flex items-center gap-2">
                  <Icon className="h-4 w-4" />
                  {label}
                </TabsTrigger>
              ))}
            </TabsList>

            <TabsContent value="customers" className="mt-4">
              {!standings || standings.individuals.top.length === 0 ? (
                empty(UserIcon, t('dashboard.competition.startLogging'))
              ) : (
                <div className="space-y-3">
                  {standings.individuals.top.map((p) => <IndividualRow key={p.userId} p={p} />)}
                  {standings.individuals.me && (
                    <>
                      {gap}
                      <IndividualRow p={standings.individuals.me} />
                    </>
                  )}
                </div>
              )}
            </TabsContent>

            <TabsContent value="groups" className="mt-4">
              {containers(standings?.groups, Users, 'group')}
            </TabsContent>

            <TabsContent value="sections" className="mt-4">
              {containers(standings?.sections, Building, 'section')}
            </TabsContent>
          </Tabs>
        )}
      </CardContent>
      <BeneficiaryCompetitionDetailsDialog
        competition={competition}
        organizationId={organizationId}
        currentUserId={currentUserId}
        open={detailsOpen}
        onOpenChange={setDetailsOpen}
      />
    </Card>
  );
}
